import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bot,
  Play,
  Pause,
  Settings,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Zap,
  BarChart3,
  RefreshCw,
  X,
  ChevronDown,
  Check
} from 'lucide-react';
import { DatabaseRobotState } from '../hooks/useDatabase';
import { supabase } from '../lib/supabaseClient';
import { useMarketData } from '../contexts/MarketDataContext';
import { Editorial, EditorialInverted, BorderRadius, Typography } from '../styles/primitives';

interface ArbitrageRobotPageProps {
  usdtBalance: number;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  robotState: DatabaseRobotState | null;
  updateBalances: (updates: { usdt_balance?: number, transaction?: any }) => void;
  addTransaction: (transaction: any) => void;
  updateRobotState: (updates: Partial<DatabaseRobotState>) => void;
  usedMargin?: number;
  availableBalance?: number;
}

interface ArbitrageOpportunity {
  id: string;
  buyExchange: string;
  sellExchange: string;
  pair: string;
  buyPrice: number;
  sellPrice: number;
  profitPercentage: number;
  timestamp: Date;
}

const ArbitrageRobotPage: React.FC<ArbitrageRobotPageProps> = ({
  usdtBalance,
  selectedPair,
  setSelectedPair,
  robotState,
  updateBalances,
  addTransaction,
  updateRobotState,
  usedMargin = 0,
  availableBalance
}) => {
  const { t } = useTranslation();
  const { marketData, isConnected: isRealtimeConnected, getSnapshotPriceBySymbol } = useMarketData();

  // Robot configuration state
  const [isActivating, setIsActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isAllocating, setIsAllocating] = useState(false);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTradingLogs, setShowTradingLogs] = useState(false);
  const [isRemovingFunds, setIsRemovingFunds] = useState(false);
  const [showInvestmentTiers, setShowInvestmentTiers] = useState(false);
  const [localAllocatedBalance, setLocalAllocatedBalance] = useState(robotState?.allocated_balance || 0);
  const [isPairDropdownOpen, setIsPairDropdownOpen] = useState(false);
  const [isStrategyDropdownOpen, setIsStrategyDropdownOpen] = useState(false);

  const actualAvailableBalance = React.useMemo(() => {
    return availableBalance !== undefined
      ? availableBalance
      : usdtBalance - localAllocatedBalance - usedMargin;
  }, [availableBalance, usdtBalance, localAllocatedBalance, usedMargin]);
  
  // Simulated trading logs
  const [tradingLogs, setTradingLogs] = useState<any[]>([]);

  // Simulated arbitrage opportunities
  const [possibleTrades, setPossibleTrades] = useState<ArbitrageOpportunity[]>([]);

  useEffect(() => {
    setLocalAllocatedBalance(robotState?.allocated_balance || 0);
  }, [robotState?.allocated_balance]);

  useEffect(() => {
    const currentAmount = parseFloat(allocationAmount);
    if (!isNaN(currentAmount) && currentAmount > actualAvailableBalance) {
      setAllocationAmount('');
    }
  }, [actualAvailableBalance, allocationAmount]);

  // Get current price for selected pair using snapshot
  const getCurrentPrice = useCallback(() => {
    return getSnapshotPriceBySymbol(selectedPair) || 0;
  }, [getSnapshotPriceBySymbol, selectedPair]);
  
  // Generate simulated arbitrage opportunities
  useEffect(() => {
    const generateArbitrageOpportunity = () => {
      const exchanges = ['Binance', 'Bybit', 'Kraken', 'Coinbase', 'Kucoin', 'OKX', 'Huobi'];
      const pairs = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT', 'BNB/USDT', 'ADA/USDT', 'AVAX/USDT'];
      
      const buyExchange = exchanges[Math.floor(Math.random() * exchanges.length)];
      let sellExchange = buyExchange;
      while (sellExchange === buyExchange) {
        sellExchange = exchanges[Math.floor(Math.random() * exchanges.length)];
      }
      
      const pair = pairs[Math.floor(Math.random() * pairs.length)];
      const currentPrice = getCurrentPrice();
      const basePrice = pair === selectedPair ? currentPrice : 
                        pair === 'BTC/USDT' ? currentPrice * 2 :
                        pair === 'ETH/USDT' ? currentPrice * 0.05 :
                        pair === 'SOL/USDT' ? currentPrice * 0.001 :
                        pair === 'XRP/USDT' ? currentPrice * 0.00001 :
                        pair === 'BNB/USDT' ? currentPrice * 0.01 :
                        pair === 'ADA/USDT' ? currentPrice * 0.00005 :
                        currentPrice * 0.0005;
      
      const buyPrice = basePrice * (1 - Math.random() * 0.005);
      const sellPrice = basePrice * (1 + Math.random() * 0.005);
      const profitPercentage = ((sellPrice - buyPrice) / buyPrice) * 100;
      
      return {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        buyExchange,
        sellExchange,
        pair,
        buyPrice,
        sellPrice,
        profitPercentage,
        timestamp: new Date()
      };
    };
    
    // Generate initial opportunities
    if (possibleTrades.length === 0) {
      const initialOpportunities = Array.from({ length: 5 }, (_, i) => {
        const opportunity = generateArbitrageOpportunity();
        opportunity.timestamp = new Date(Date.now() - i * 30000); // 30 seconds apart
        return opportunity;
      });
      setPossibleTrades(initialOpportunities);
    }
    
    // Add new opportunities periodically if robot is active
    const interval = setInterval(() => {
      if (robotState?.is_active) {
        setPossibleTrades(prev => {
          const newOpportunity = generateArbitrageOpportunity();
          return [newOpportunity, ...prev].slice(0, 10); // Keep only the 10 most recent
        });
      }
    }, 45000); // Every 45 seconds
    
    return () => clearInterval(interval);
  }, [robotState?.is_active, getCurrentPrice, possibleTrades.length]);
  
  // Generate simulated trading logs
  useEffect(() => {
    if (robotState?.is_active) {
      const generateLog = () => {
        const actions = ['BUY', 'SELL'];
        const exchanges = ['Binance', 'Bybit', 'Kraken', 'Coinbase'];
        const pairs = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT'];
        
        const action = actions[Math.floor(Math.random() * actions.length)];
        const exchange = exchanges[Math.floor(Math.random() * exchanges.length)];
        const pair = pairs[Math.floor(Math.random() * pairs.length)];
        const amount = (Math.random() * 0.1).toFixed(6);
        const currentPrice = getCurrentPrice();
        const price = (action === 'BUY' ? currentPrice * 0.9999 : currentPrice * 1.0001).toFixed(2);
        const profit = (Math.random() * 0.01).toFixed(6);
        
        return {
          id: Date.now().toString(),
          action,
          pair,
          exchange,
          amount,
          price,
          profit,
          timestamp: new Date().toISOString()
        };
      };
      
      // Generate a new log every 2 minutes if robot is active
      const interval = setInterval(() => {
        const newLog = generateLog();
        setTradingLogs(prev => [newLog, ...prev].slice(0, 100));
      }, 120000);
      
      // Generate initial logs
      if (tradingLogs.length === 0) {
        const initialLogs = Array.from({ length: 10 }, (_, i) => {
          const log = generateLog();
          log.timestamp = new Date(Date.now() - i * 60000).toISOString();
          return log;
        });
        setTradingLogs(initialLogs);
      }
      
      return () => clearInterval(interval);
    }
  }, [robotState?.is_active, getCurrentPrice, tradingLogs.length]);

  // Handle robot activation
  const handleActivateRobot = async () => {
    if (!localAllocatedBalance || localAllocatedBalance <= 0) {
      setError(t('robot.pleaseAllocateBalance'));
      return;
    }
    
    setIsActivating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update robot state
      await updateRobotState({
        is_active: true,
        todays_profit: 0, // Reset daily profit on activation
        last_profit_timestamp: new Date().toISOString()
      });
      
      setSuccess(t('robot.robotActivatedSuccessfully'));
    } catch (error: any) {
      setError(error.message || t('robot.failedToActivateRobot'));
    } finally {
      setIsActivating(false);
    }
  };

  // Handle robot deactivation
  const handleDeactivateRobot = async () => {
    setIsDeactivating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update robot state
      await updateRobotState({
        is_active: false
      });
      
      setSuccess(t('robot.robotDeactivatedSuccessfully'));
    } catch (error: any) {
      setError(error.message || t('robot.failedToDeactivateRobot'));
    } finally {
      setIsDeactivating(false);
    }
  };

  // Handle balance allocation
  const handleAllocateBalance = async () => {
    const amount = parseFloat(allocationAmount);

    if (isNaN(amount) || amount <= 0) {
      setError(t('errors.invalidAmount'));
      return;
    }

    if (amount > actualAvailableBalance) {
      setError(t('errors.insufficientBalance'));
      return;
    }
    
    setIsAllocating(true);
    setError(null);
    setSuccess(null);

    const newAllocatedBalance = localAllocatedBalance + amount;
    setLocalAllocatedBalance(newAllocatedBalance);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Update balances
      await updateBalances({
        usdt_balance: usdtBalance - amount,
        transaction: {
          type: 'robot_allocation',
          amount: -amount,
          description: t('robot.allocatedBalanceDescription'),
          status: 'completed'
        }
      });

      // Update robot state
      await updateRobotState({
        allocated_balance: newAllocatedBalance
      });

      setSuccess(t('robot.successfullyAllocated', { amount: amount.toFixed(2) }));
      setAllocationAmount('');
      setShowAllocationForm(false);
    } catch (error: any) {
      setLocalAllocatedBalance(localAllocatedBalance);
      setError(error.message || t('robot.failedToAllocateBalance'));
    } finally {
      setIsAllocating(false);
    }
  };

  // Handle removing allocated balance
  const handleRemoveAllocatedFunds = async () => {
    setIsRemovingFunds(true);
    setError(null);
    setSuccess(null);

    const allocatedAmount = localAllocatedBalance;

    if (allocatedAmount <= 0) {
      setError(t('robot.noAllocatedFundsToRemove'));
      setIsRemovingFunds(false);
      return;
    }

    setLocalAllocatedBalance(0);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Return allocated balance to user's main USDT balance
      await updateBalances({
        usdt_balance: usdtBalance + allocatedAmount,
        transaction: {
          type: 'robot_withdrawal',
          amount: allocatedAmount,
          description: t('robot.fundsRemovedDescription'),
          status: 'completed'
        }
      });

      // Reset robot state
      await updateRobotState({
        allocated_balance: 0,
        todays_profit: 0
      });

      setSuccess(t('robot.successfullyRemoved', { amount: allocatedAmount.toFixed(2) }));
    } catch (error: any) {
      setLocalAllocatedBalance(allocatedAmount);
      setError(error.message || t('robot.failedToRemoveFunds'));
    } finally {
      setIsRemovingFunds(false);
    }
  };

  // Calculate daily profit percentage based on allocated balance (in BTC equivalent)
  const getDailyProfitPercentage = () => {
    const allocatedBalance = localAllocatedBalance;
    const btcPrice = getCurrentPrice() || 50000; // Get current BTC price or fallback
    const allocatedBalanceInBTC = allocatedBalance / btcPrice;

    if (allocatedBalanceInBTC >= 50) return 2.1; // WHALE tier (50 BTC)
    if (allocatedBalanceInBTC >= 10) return 1.2; // SHARK tier (10 BTC)
    if (allocatedBalanceInBTC >= 5) return 0.8; // DOLPHIN tier (5 BTC)
    if (allocatedBalanceInBTC >= 1) return 0.6; // OCTOPUS tier (1 BTC)
    if (allocatedBalanceInBTC >= 0.1) return 0.35; // CRAB tier (0.1 BTC)
    return 0.2; // SHRIMP tier (0.01 BTC)
  };

  // Calculate estimated daily profit
  const estimatedDailyProfit = localAllocatedBalance * (getDailyProfitPercentage() / 100);
  
  // Calculate estimated monthly profit
  const estimatedMonthlyProfit = estimatedDailyProfit * 30;
  
  // Calculate estimated yearly profit
  const estimatedYearlyProfit = estimatedDailyProfit * 365;
  
  // Get investment tier based on allocated balance (in BTC equivalent)
  const getInvestmentTier = () => {
    const allocatedBalance = localAllocatedBalance;
    const btcPrice = getCurrentPrice() || 50000; // Get current BTC price or fallback
    const allocatedBalanceInBTC = allocatedBalance / btcPrice;

    if (allocatedBalanceInBTC >= 50) return 'WHALE';
    if (allocatedBalanceInBTC >= 10) return 'SHARK';
    if (allocatedBalanceInBTC >= 5) return 'DOLPHIN';
    if (allocatedBalanceInBTC >= 1) return 'OCTOPUS';
    if (allocatedBalanceInBTC >= 0.1) return 'CRAB';
    if (allocatedBalanceInBTC > 0) return 'SHRIMP';
    return 'NONE';
  };

  // Get tier color
  const getTierColor = () => {
    const tier = getInvestmentTier();
    
    switch (tier) {
      case 'WHALE': return 'text-green-400';
      case 'SHARK': return 'text-red-400';
      case 'DOLPHIN': return 'text-blue-400';
      case 'OCTOPUS': return 'text-purple-400';
      case 'CRAB': return 'text-orange-400';
      case 'SHRIMP': return 'text-gray-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/95 relative">
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4" style={{ paddingBottom: '2rem' }}>
          <div>
            <h1
  className="flex items-center gap-3"
  style={{
    fontSize: Typography.DisplayMedium,
    fontWeight: Typography.WeightBold,
    color: '#1e293b',
  }}
>
  <Bot size={32} style={{ color: '#d91222' }} />
  {t('robot.title')}
</h1>

            <p style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.BodyMedium }}>{t('robot.subtitle')}</p>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="px-6 py-3 w-full md:w-auto" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('header.availableBalance')}</div>
              <div className="font-mono" style={{ color: '#000000', fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold }}>${actualAvailableBalance.toFixed(2)}</div>
            </div>

            <div className="flex gap-3 w-full md:w-auto">
              {robotState?.is_active ? (
                <button
                  onClick={handleDeactivateRobot}
                  disabled={isDeactivating}
                  className="flex-1 md:flex-auto px-6 py-3 font-medium flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'rgba(217, 18, 34, 0.15)',
                    color: '#d91222',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isDeactivating ? 'not-allowed' : 'pointer',
                    opacity: isDeactivating ? 0.5 : 1,
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightMedium
                  }}
                >
                  {isDeactivating ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : (
                    <Pause size={18} />
                  )}
                  {t('robot.stopRobot')}
                </button>
              ) : (
                <button
                  onClick={handleActivateRobot}
                  disabled={isActivating || !localAllocatedBalance || localAllocatedBalance <= 0}
                  className="flex-1 md:flex-auto px-6 py-3 font-medium flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: isActivating || !localAllocatedBalance || localAllocatedBalance <= 0 ? 'rgba(255, 255, 255, 0.05)' : EditorialInverted.Text,
                    color: isActivating || !localAllocatedBalance || localAllocatedBalance <= 0 ? EditorialInverted.TextSecondary : EditorialInverted.Surface,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isActivating || !localAllocatedBalance || localAllocatedBalance <= 0 ? 'not-allowed' : 'pointer',
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightMedium
                  }}
                >
                  {isActivating ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : (
                    <Play size={18} />
                  )}
                  {t('robot.startRobot')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 flex items-center gap-3" style={{ border: 'none', borderRadius: '8px', backgroundColor: 'rgba(217, 18, 34, 0.15)' }}>
            <AlertTriangle size={20} style={{ color: '#d91222' }} className="flex-shrink-0" />
            <span style={{ color: '#d91222', fontSize: Typography.BodyMedium }}>{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 flex items-center gap-3" style={{ border: 'none', borderRadius: '8px', backgroundColor: 'rgba(22, 163, 74, 0.15)' }}>
            <CheckCircle size={20} style={{ color: Editorial.Profit }} className="flex-shrink-0" />
            <span style={{ color: Editorial.Profit, fontSize: Typography.BodyMedium }}>{success}</span>
          </div>
        )}

        {/* Main Content - Restructured Layout */}

        {/* Hero Status Banner - Full Width */}
        <div className="mb-6 p-8 relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '20px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
        }}>
          {/* Decorative background pattern */}
          <div className="absolute top-0 right-0 w-64 h-64 opacity-5" style={{
            background: 'radial-gradient(circle, #d91222 0%, transparent 70%)',
            transform: 'translate(30%, -30%)'
          }} />

          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-4">
              <div className="p-4" style={{
                background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(217, 18, 34, 0.3)'
              }}>
                <Bot size={40} style={{ color: '#ffffff' }} />
              </div>
              <div>
                <h2 style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#1e293b' }}>{t('robot.robotStatus')}</h2>
                <p style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.BodyMedium, marginTop: '0.25rem' }}>
                  {t('robot.investmentTier')}: <span style={{ fontWeight: Typography.WeightSemibold, color: '#d91222' }}>{getInvestmentTier()}</span> • {t('robot.dailyProfitRate')}: <span style={{ fontWeight: Typography.WeightSemibold, color: '#16a34a' }}>{getDailyProfitPercentage()}%</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="px-6 py-3 flex items-center gap-2" style={{
                borderRadius: '12px',
                fontSize: Typography.BodyMedium,
                fontWeight: Typography.WeightBold,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                background: robotState?.is_active
                  ? 'linear-gradient(135deg, rgba(22, 163, 74, 0.15) 0%, rgba(22, 163, 74, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(217, 18, 34, 0.15) 0%, rgba(217, 18, 34, 0.05) 100%)',
                border: robotState?.is_active ? '1px solid rgba(22, 163, 74, 0.3)' : '1px solid rgba(217, 18, 34, 0.3)',
                color: robotState?.is_active ? '#16a34a' : '#d91222',
                textAlign: 'center',
                boxShadow: robotState?.is_active ? '0 2px 8px rgba(22, 163, 74, 0.15)' : '0 2px 8px rgba(217, 18, 34, 0.15)'
              }}>
                <Activity size={18} className={robotState?.is_active ? 'animate-pulse' : ''} />
                {robotState?.is_active ? t('robot.active') : t('robot.inactive')}
              </div>
            </div>
          </div>
        </div>

        {/* Profit Projections - FULL WIDTH HIGHLIGHT */}
        <div className="mb-6 p-6 relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '20px',
          boxShadow: '0 4px 16px rgba(16, 185, 129, 0.1)'
        }}>
          {/* Decorative background */}
          <div className="absolute bottom-0 left-0 w-96 h-96 opacity-5" style={{
            background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
            transform: 'translate(-30%, 30%)'
          }} />

          <h3 className="mb-6 flex items-center gap-2 relative z-10" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b' }}>
            <div className="p-2" style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrendingUp size={20} style={{ color: '#ffffff' }} />
            </div>
            {t('robot.profitProjections')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
            <div className="p-6 text-center relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
            }}>
              <div className="absolute top-2 right-2 opacity-30">
                <Zap size={40} style={{ color: '#10b981' }} />
              </div>
              <div className="mb-2" style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: Typography.WeightSemibold }}>{t('robot.daily')}</div>
              <div className="font-bold font-mono" style={{ color: '#10b981', fontSize: Typography.DisplayMedium }}>+${estimatedDailyProfit.toFixed(2)}</div>
              <div style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.6)', marginTop: '0.5rem' }}>{t('robot.ofAllocatedBalance', { percentage: getDailyProfitPercentage() })}</div>
            </div>
            <div className="p-6 text-center relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
            }}>
              <div className="absolute top-2 right-2 opacity-30">
                <Target size={40} style={{ color: '#10b981' }} />
              </div>
              <div className="mb-2" style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: Typography.WeightSemibold }}>{t('robot.monthly')}</div>
              <div className="font-bold font-mono" style={{ color: '#10b981', fontSize: Typography.DisplayMedium }}>+${estimatedMonthlyProfit.toFixed(2)}</div>
              <div style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.6)', marginTop: '0.5rem' }}>{t('robot.ofAllocatedBalance', { percentage: (getDailyProfitPercentage() * 30).toFixed(1) })}</div>
            </div>
            <div className="p-6 text-center relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
            }}>
              <div className="absolute top-2 right-2 opacity-30">
                <TrendingUp size={40} style={{ color: '#10b981' }} />
              </div>
              <div className="mb-2" style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: Typography.WeightSemibold }}>{t('robot.yearly')}</div>
              <div className="font-bold font-mono" style={{ color: '#10b981', fontSize: Typography.DisplayMedium }}>+${estimatedYearlyProfit.toFixed(2)}</div>
              <div style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.6)', marginTop: '0.5rem' }}>{t('robot.ofAllocatedBalance', { percentage: (getDailyProfitPercentage() * 365).toFixed(1) })}</div>
            </div>
          </div>
        </div>

        {/* Main Content Grid - 3 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Row 1: Today's Profit, Allocated Balance, How It Works (spans 2 rows) */}
              {/* Today's Profit */}
              <div className="p-6 relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
              }}>
                <div className="absolute top-0 right-0 w-32 h-32 opacity-5" style={{
                  background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
                  transform: 'translate(20%, -20%)'
                }} />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="p-3" style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                  }}>
                    <TrendingUp size={24} style={{ color: '#ffffff' }} />
                  </div>
                  <div>
                    <div style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: Typography.WeightSemibold }}>{t('robot.todaysProfit')}</div>
                    <div className="font-bold font-mono" style={{ color: '#10b981', fontSize: Typography.DisplaySmall }}>+${(robotState?.todays_profit || 0).toFixed(2)}</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2 relative z-10">
                  <div className="flex justify-between" style={{ fontSize: Typography.BodySmall }}>
                    <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('robot.totalTrades')}</span>
                    <span className="font-mono font-medium" style={{ color: '#000000', fontWeight: Typography.WeightSemibold }}>{robotState?.total_trades || 0}</span>
                  </div>
                  <div className="flex justify-between" style={{ fontSize: Typography.BodySmall }}>
                    <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('robot.successRate')}</span>
                    <span className="font-mono font-medium" style={{ color: '#000000', fontWeight: Typography.WeightSemibold }}>
                      {robotState?.total_trades && robotState.total_trades > 0
                        ? ((robotState?.successful_trades || 0) / robotState.total_trades * 100).toFixed(1)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Allocated Balance */}
              <div className="p-6 relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
              }}>
                <div className="absolute top-0 right-0 w-32 h-32 opacity-5" style={{
                  background: 'radial-gradient(circle, #d91222 0%, transparent 70%)',
                  transform: 'translate(20%, -20%)'
                }} />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="p-3" style={{
                    background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(217, 18, 34, 0.25)'
                  }}>
                    <DollarSign size={24} style={{ color: '#ffffff' }} />
                  </div>
                  <div>
                    <div style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: Typography.WeightSemibold }}>{t('robot.allocatedBalance')}</div>
                    <div className="font-bold font-mono" style={{ color: '#1e293b', fontSize: Typography.DisplaySmall }}>${localAllocatedBalance.toFixed(2)}</div>
                  </div>
                </div>


                <div className="flex flex-col gap-2 mt-4 relative z-10">
                  <button
                    onClick={() => setShowAllocationForm(!showAllocationForm)}
                    className="w-full px-4 py-2 flex items-center justify-center gap-2"
                    style={{
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1) 0%, rgba(217, 18, 34, 0.05) 100%)',
                      color: '#d91222',
                      border: '1px solid rgba(217, 18, 34, 0.3)',
                      borderRadius: '8px',
                      fontSize: Typography.BodySmall,
                      fontWeight: Typography.WeightMedium,
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(217, 18, 34, 0.1)'
                    }}
                  >
                    {showAllocationForm ? t('common.cancel') : t('robot.allocateFunds')}
                  </button>
                  <button
                    onClick={() => setShowInvestmentTiers(true)}
                    className="w-full px-4 py-2 flex items-center gap-2 justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                      color: '#1e293b',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      fontSize: Typography.BodySmall,
                      fontWeight: Typography.WeightMedium,
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
                    }}
                  >
                    <BarChart3 size={16} />
                    {t('robot.viewInvestmentTiers')}
                  </button>
                </div>

                  {/* Remove Allocated Funds Button - Only show when robot is off and has allocated balance */}
                  {!robotState?.is_active && localAllocatedBalance > 0 && (
                    <div className="mt-3 relative z-10">
                      <button
                        onClick={handleRemoveAllocatedFunds}
                        disabled={isRemovingFunds}
                        className="w-full px-4 py-2 flex items-center justify-center gap-2"
                        style={{
                          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                          color: '#1e293b',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          fontSize: Typography.BodySmall,
                          fontWeight: Typography.WeightMedium,
                          cursor: isRemovingFunds ? 'not-allowed' : 'pointer',
                          opacity: isRemovingFunds ? 0.5 : 1,
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
                        }}
                      >
                        {isRemovingFunds ? (
                          <>
                            <RefreshCw size={16} className="animate-spin" />
                            {t('robot.processing')}...
                          </>
                        ) : (
                          <>
                            <TrendingDown size={16} />
                            {t('robot.removeAllocatedFunds')}
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {showAllocationForm && (
                    <div className="mt-4 p-4 relative z-10" style={{
                      border: '1px solid rgba(217, 18, 34, 0.2)',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.05) 0%, rgba(217, 18, 34, 0.02) 100%)',
                      boxShadow: '0 2px 8px rgba(217, 18, 34, 0.08)'
                    }}>
                      <div className="mb-3">
                        <label className="block mb-1" style={{ fontSize: Typography.BodySmall, color: '#1e293b', fontWeight: Typography.WeightMedium }}>{t('robot.amountToAllocate')}</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={allocationAmount}
                            onChange={(e) => setAllocationAmount(e.target.value)}
                            placeholder={t('swap.enterAmount')}
                            className="flex-1 px-3 py-2 font-mono focus:outline-none"
                            style={{
                              background: '#ffffff',
                              color: '#000000',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '8px',
                              fontSize: Typography.BodyMedium
                            }}
                          />
                          <button
                            onClick={() => setAllocationAmount(actualAvailableBalance.toString())}
                            className="px-3 py-2"
                            style={{
                              background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15) 0%, rgba(217, 18, 34, 0.1) 100%)',
                              color: '#d91222',
                              border: '1px solid rgba(217, 18, 34, 0.3)',
                              borderRadius: '8px',
                              fontSize: Typography.Fine,
                              fontWeight: Typography.WeightMedium,
                              cursor: 'pointer'
                            }}
                          >
                            {t('common.max')}
                          </button>
                        </div>
                        {parseFloat(allocationAmount) > actualAvailableBalance && (
                          <p style={{ color: '#d91222', fontSize: Typography.Fine, marginTop: '0.5rem' }}>
                            {t('errors.insufficientBalance')} (Max: ${actualAvailableBalance.toFixed(2)})
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleAllocateBalance}
                        disabled={isAllocating || parseFloat(allocationAmount) > actualAvailableBalance || !allocationAmount}
                        className="w-full py-2 flex items-center justify-center gap-2"
                        style={{
                          backgroundColor: isAllocating || parseFloat(allocationAmount) > actualAvailableBalance || !allocationAmount ? 'rgba(255, 255, 255, 0.05)' : 'rgba(217, 18, 34, 0.25)',
                          color: isAllocating || parseFloat(allocationAmount) > actualAvailableBalance || !allocationAmount ? EditorialInverted.TextSecondary : '#d91222',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: Typography.BodySmall,
                          fontWeight: Typography.WeightMedium,
                          cursor: isAllocating || parseFloat(allocationAmount) > actualAvailableBalance || !allocationAmount ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {isAllocating ? (
                          <RefreshCw size={16} className="animate-spin" />
                        ) : (
                          t('robot.confirmAllocation')
                        )}
                      </button>
                    </div>
                  )}
              </div>

          {/* How It Works - Spans 2 rows on large screens */}
          <div className="lg:row-span-2 p-6 relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
          }}>
            <div className="absolute top-0 right-0 w-32 h-32 opacity-5" style={{
              background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
              transform: 'translate(30%, -30%)'
            }} />
            <h2 className="mb-6 flex items-center gap-2 relative z-10" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b' }}>
              <div className="p-2" style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Zap size={18} style={{ color: '#ffffff' }} />
              </div>
              {t('robot.howItWorks')}
            </h2>

            <div className="space-y-6 relative z-10">
              <div>
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex items-center justify-center font-mono flex-shrink-0" style={{
                    color: '#ffffff',
                    fontWeight: Typography.WeightBold,
                    fontSize: Typography.BodyMedium,
                    background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    boxShadow: '0 2px 8px rgba(217, 18, 34, 0.3)'
                  }}>
                    1
                  </div>
                  <div>
                    <h3 className="mb-2" style={{ fontSize: Typography.HeadlineSmall, fontWeight: Typography.WeightBold, color: '#1e293b' }}>{t('robot.marketAnalysis')}</h3>
                    <p style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodySmall, lineHeight: Typography.LeadingRelaxed }}>
                      {t('robot.marketAnalysisDescription')}
                    </p>
                    <p className="mt-2" style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.Fine, lineHeight: Typography.LeadingRelaxed }}>
                      Our advanced AI algorithms continuously scan major exchanges including Binance, Coinbase, Kraken, Bybit, and 11+ others. The system analyzes price movements, trading volumes, and market depth in real-time to identify even the smallest profitable price discrepancies across different trading pairs.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex items-center justify-center font-mono flex-shrink-0" style={{
                    color: '#ffffff',
                    fontWeight: Typography.WeightBold,
                    fontSize: Typography.BodyMedium,
                    background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    boxShadow: '0 2px 8px rgba(217, 18, 34, 0.3)'
                  }}>
                    2
                  </div>
                  <div>
                    <h3 className="mb-2" style={{ fontSize: Typography.HeadlineSmall, fontWeight: Typography.WeightBold, color: '#1e293b' }}>{t('robot.opportunityDetection')}</h3>
                    <p style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodySmall, lineHeight: Typography.LeadingRelaxed }}>
                      {t('robot.opportunityDetectionDescription')}
                    </p>
                    <p className="mt-2" style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.Fine, lineHeight: Typography.LeadingRelaxed }}>
                      The system employs sophisticated mathematical models to detect arbitrage opportunities. When a price difference between exchanges exceeds your configured threshold (accounting for trading fees and slippage), the opportunity is flagged and prepared for execution. The detection engine processes thousands of price points per second.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex items-center justify-center font-mono flex-shrink-0" style={{
                    color: '#ffffff',
                    fontWeight: Typography.WeightBold,
                    fontSize: Typography.BodyMedium,
                    background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    boxShadow: '0 2px 8px rgba(217, 18, 34, 0.3)'
                  }}>
                    3
                  </div>
                  <div>
                    <h3 className="mb-2" style={{ fontSize: Typography.HeadlineSmall, fontWeight: Typography.WeightBold, color: '#1e293b' }}>{t('robot.automatedExecution')}</h3>
                    <p style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodySmall, lineHeight: Typography.LeadingRelaxed }}>
                      {t('robot.automatedExecutionDescription')}
                    </p>
                    <p className="mt-2" style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.Fine, lineHeight: Typography.LeadingRelaxed }}>
                      Once an opportunity is identified, the robot instantly executes simultaneous buy and sell orders across multiple exchanges. The entire process happens in milliseconds using low-latency connections and optimized order routing, ensuring you capture the profit window before market conditions change.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex items-center justify-center font-mono flex-shrink-0" style={{
                    color: '#ffffff',
                    fontWeight: Typography.WeightBold,
                    fontSize: Typography.BodyMedium,
                    background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    boxShadow: '0 2px 8px rgba(217, 18, 34, 0.3)'
                  }}>
                    4
                  </div>
                  <div>
                    <h3 className="mb-2" style={{ fontSize: Typography.HeadlineSmall, fontWeight: Typography.WeightBold, color: '#1e293b' }}>{t('robot.profitAccumulation')}</h3>
                    <p style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodySmall, lineHeight: Typography.LeadingRelaxed }}>
                      {t('robot.profitAccumulationDescription')}
                    </p>
                    <p className="mt-2" style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.Fine, lineHeight: Typography.LeadingRelaxed }}>
                      All profits from successful arbitrage trades are automatically credited to your account balance. The system compounds your earnings by reinvesting within your allocated balance limits. Higher tier investments unlock access to more trading pairs, larger position sizes, and priority execution during high-volatility periods.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Robot Settings */}
          <div className="lg:col-span-2 p-6 relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
          }}>
            <div className="absolute top-0 right-0 w-32 h-32 opacity-5" style={{
              background: 'radial-gradient(circle, #d91222 0%, transparent 70%)',
              transform: 'translate(30%, -30%)'
            }} />
            <h2 className="mb-6 flex items-center gap-2 relative z-10" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b' }}>
              <div className="p-2" style={{
                background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Settings size={18} style={{ color: '#ffffff' }} />
              </div>
              {t('robot.robotSettings')}
            </h2>

            <div className="space-y-4 relative z-10">
              <div>
                <label className="block mb-2" style={{ fontSize: Typography.BodySmall, color: '#1e293b', fontWeight: Typography.WeightMedium }}>{t('robot.tradingPair')}</label>
                <div className="relative">
                    <button
                      onClick={() => setIsPairDropdownOpen(!isPairDropdownOpen)}
                      className="w-full px-4 py-3 font-mono focus:outline-none cursor-pointer transition-all duration-200 flex items-center justify-between"
                      style={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        color: '#1e293b',
                        border: isPairDropdownOpen ? '1px solid #d91222' : '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: '10px',
                        fontSize: Typography.BodyMedium,
                        fontWeight: Typography.WeightMedium,
                        boxShadow: isPairDropdownOpen ? '0 0 0 3px rgba(217, 18, 34, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isPairDropdownOpen) {
                          e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.12)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isPairDropdownOpen) {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                        }
                      }}
                    >
                      <span>{selectedPair}</span>
                      <ChevronDown
                        size={16}
                        style={{
                          opacity: 0.6,
                          transition: 'transform 0.2s',
                          transform: isPairDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                      />
                    </button>

                    {isPairDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setIsPairDropdownOpen(false)}
                        />
                        <div
                          className="absolute top-full left-0 right-0 mt-2 z-20 overflow-hidden"
                          style={{
                            background: '#ffffff',
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: '10px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                            maxHeight: '300px',
                            overflowY: 'auto'
                          }}
                        >
                          {marketData.map((pair) => (
                            <button
                              key={pair.symbol}
                              onClick={() => {
                                setSelectedPair(pair.symbol);
                                setIsPairDropdownOpen(false);
                              }}
                              className="w-full px-4 py-3 font-mono text-left flex items-center justify-between transition-all duration-150"
                              style={{
                                background: selectedPair === pair.symbol ? 'linear-gradient(135deg, rgba(217, 18, 34, 0.08) 0%, rgba(217, 18, 34, 0.04) 100%)' : 'transparent',
                                color: '#1e293b',
                                border: 'none',
                                fontSize: Typography.BodyMedium,
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                if (selectedPair !== pair.symbol) {
                                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.02) 100%)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedPair !== pair.symbol) {
                                  e.currentTarget.style.background = 'transparent';
                                } else {
                                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 18, 34, 0.08) 0%, rgba(217, 18, 34, 0.04) 100%)';
                                }
                              }}
                            >
                              <span>{pair.symbol}</span>
                              {selectedPair === pair.symbol && (
                                <Check size={16} style={{ color: '#d91222' }} />
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-2" style={{ fontSize: Typography.BodySmall, color: '#1e293b', fontWeight: Typography.WeightMedium }}>{t('robot.tradingStrategy')}</label>
                  <div className="relative">
                    <button
                      onClick={() => setIsStrategyDropdownOpen(!isStrategyDropdownOpen)}
                      className="w-full px-4 py-3 focus:outline-none cursor-pointer transition-all duration-200 flex items-center justify-between"
                      style={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        color: '#1e293b',
                        border: isStrategyDropdownOpen ? '1px solid #d91222' : '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: '10px',
                        fontSize: Typography.BodyMedium,
                        fontWeight: Typography.WeightMedium,
                        boxShadow: isStrategyDropdownOpen ? '0 0 0 3px rgba(217, 18, 34, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isStrategyDropdownOpen) {
                          e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.12)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isStrategyDropdownOpen) {
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                        }
                      }}
                    >
                      <span>{t(`robot.strategies.${robotState?.strategy || 'triangular'}`)}</span>
                      <ChevronDown
                        size={16}
                        style={{
                          opacity: 0.6,
                          transition: 'transform 0.2s',
                          transform: isStrategyDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                      />
                    </button>

                    {isStrategyDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setIsStrategyDropdownOpen(false)}
                        />
                        <div
                          className="absolute top-full left-0 right-0 mt-2 z-20 overflow-hidden"
                          style={{
                            background: '#ffffff',
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: '10px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                          }}
                        >
                          {[
                            { value: 'triangular', label: t('robot.strategies.triangular') },
                            { value: 'spatial', label: t('robot.strategies.spatial') },
                            { value: 'statistical', label: t('robot.strategies.statistical') },
                            { value: 'latency', label: t('robot.strategies.latency') }
                          ].map((strategy) => (
                            <button
                              key={strategy.value}
                              onClick={() => {
                                updateRobotState({ strategy: strategy.value });
                                setIsStrategyDropdownOpen(false);
                              }}
                              className="w-full px-4 py-3 text-left flex items-center justify-between transition-all duration-150"
                              style={{
                                background: (robotState?.strategy || 'triangular') === strategy.value ? 'linear-gradient(135deg, rgba(217, 18, 34, 0.08) 0%, rgba(217, 18, 34, 0.04) 100%)' : 'transparent',
                                color: '#1e293b',
                                border: 'none',
                                fontSize: Typography.BodyMedium,
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                if ((robotState?.strategy || 'triangular') !== strategy.value) {
                                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.02) 100%)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if ((robotState?.strategy || 'triangular') !== strategy.value) {
                                  e.currentTarget.style.background = 'transparent';
                                } else {
                                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 18, 34, 0.08) 0%, rgba(217, 18, 34, 0.04) 100%)';
                                }
                              }}
                            >
                              <span>{strategy.label}</span>
                              {(robotState?.strategy || 'triangular') === strategy.value && (
                                <Check size={16} style={{ color: '#d91222' }} />
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-2" style={{ fontSize: Typography.BodySmall, color: '#1e293b', fontWeight: Typography.WeightMedium }}>
                    {t('robot.minProfitThreshold')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.1"
                    max="5"
                    value={robotState?.min_profit_threshold || 0.5}
                    onChange={(e) => updateRobotState({ min_profit_threshold: parseFloat(e.target.value) })}
                    className="w-full px-4 py-3 font-mono focus:outline-none"
                    style={{
                      background: '#ffffff',
                      color: '#000000',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      fontSize: Typography.BodyMedium
                    }}
                  />
                  <p className="mt-1" style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.6)' }}>
                    {t('robot.minProfitThresholdDescription')}
                  </p>
                </div>

                <div>
                  <label className="block mb-2" style={{ fontSize: Typography.BodySmall, color: '#1e293b', fontWeight: Typography.WeightMedium }}>
                    {t('robot.maxTradeAmount')}
                  </label>
                  <input
                    type="number"
                    step="10"
                    min="10"
                    max={localAllocatedBalance || 1000}
                    value={robotState?.max_trade_amount || 1000}
                    onChange={(e) => updateRobotState({ max_trade_amount: parseFloat(e.target.value) })}
                    className="w-full px-4 py-3 font-mono focus:outline-none"
                    style={{
                      background: '#ffffff',
                      color: '#000000',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      fontSize: Typography.BodyMedium
                    }}
                  />
                  <p className="mt-1" style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.6)' }}>
                    {t('robot.maxTradeAmountDescription', { min: 10, max: localAllocatedBalance || 1000 })}
                  </p>
                </div>
              </div>

              <button
                onClick={() => updateRobotState({
                  strategy: robotState?.strategy || 'triangular',
                  min_profit_threshold: robotState?.min_profit_threshold || 0.5,
                  max_trade_amount: robotState?.max_trade_amount || 1000
                })}
                className="w-full mt-6 py-3 flex items-center justify-center gap-2 relative z-10"
                style={{
                  background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: Typography.BodyMedium,
                  fontWeight: Typography.WeightMedium,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(217, 18, 34, 0.25)'
                }}
              >
                <Settings size={18} />
                {t('robot.saveSettings')}
              </button>
            </div>
          </div>

          {/* Possible Arbitrage Opportunities */}
            <div className="p-6 relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
            }}>
              <div className="absolute top-0 right-0 w-48 h-48 opacity-5" style={{
                background: 'radial-gradient(circle, #d91222 0%, transparent 70%)',
                transform: 'translate(30%, -30%)'
              }} />
              <div className="flex items-center justify-between mb-6 relative z-10">
                <h2 className="flex items-center gap-2" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b' }}>
                  <div className="p-2" style={{
                    background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Target size={18} style={{ color: '#ffffff' }} />
                  </div>
                  {t('robot.possibleOpportunities')}
                </h2>
                <div className="px-3 py-1" style={{
                  fontSize: Typography.Fine,
                  color: '#1e293b',
                  background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  fontWeight: Typography.WeightSemibold
                }}>
                  {t('robot.opportunitiesCount', { count: possibleTrades.length })}
                </div>
              </div>

              {possibleTrades.length > 0 ? (
                <div
                  className="space-y-3 max-h-[500px] overflow-y-auto relative z-10"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch'
                  } as React.CSSProperties & { scrollbarWidth?: string; msOverflowStyle?: string; WebkitOverflowScrolling?: string }}
                >
                  <style>{`
                    .space-y-3::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                  {possibleTrades.map((trade, index) => (
                    <div
                      key={trade.id}
                      className="py-4 px-5 grid grid-cols-6 gap-4 items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%)',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        borderRadius: '12px',
                        fontSize: Typography.BodyMedium
                      }}
                    >
                      <div className="font-mono font-bold" style={{ color: '#1e293b', fontSize: Typography.BodyMedium }}>
                        {trade.pair}
                      </div>
                      <div className="font-mono font-bold" style={{ color: '#10b981', fontSize: Typography.BodyMedium }}>
                        +{trade.profitPercentage.toFixed(2)}%
                      </div>
                      <div className="flex items-center gap-2">
                        <TrendingUp size={16} style={{ color: '#10b981' }} />
                        <span style={{ color: '#1e293b', fontSize: Typography.BodySmall }}>
                          {trade.buyExchange}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <TrendingDown size={16} style={{ color: '#d91222' }} />
                        <span style={{ color: '#1e293b', fontSize: Typography.BodySmall }}>
                          {trade.sellExchange}
                        </span>
                      </div>
                      <div className="font-mono" style={{ color: '#1e293b', fontSize: Typography.BodySmall }}>
                        ${trade.buyPrice.toFixed(2)}
                      </div>
                      <div className="font-mono text-right" style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.BodySmall }}>
                        {new Date(trade.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 relative z-10">
                  <p className="mb-2" style={{ color: '#1e293b', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightSemibold }}>{t('robot.noOpportunitiesFound')}</p>
                  <p style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.BodySmall }}>{t('robot.activateToScan')}</p>
                </div>
              )}
            </div>
        </div>

        <div className="p-6 relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
        }}>
          <div className="absolute top-0 left-0 w-48 h-48 opacity-5" style={{
            background: 'radial-gradient(circle, #d91222 0%, transparent 70%)',
            transform: 'translate(-30%, -30%)'
          }} />
          <div className="flex items-center justify-between mb-6 relative z-10">
            <h2 className="flex items-center gap-2" style={{ fontSize: Typography.HeadlineLarge, fontWeight: Typography.WeightBold, color: '#1e293b' }}>
              <div className="p-2" style={{
                background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BarChart3 size={20} style={{ color: '#ffffff' }} />
              </div>
              {t('robot.tradingLogs')}
            </h2>
            <button
              onClick={() => setShowTradingLogs(!showTradingLogs)}
              className="p-2"
              style={{
                color: '#1e293b',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '8px'
              }}
            >
              {showTradingLogs ? <TrendingDown size={20} /> : <TrendingUp size={20} />}
            </button>
          </div>

          {showTradingLogs ? (
            <div className="max-h-96 overflow-y-auto">
              {tradingLogs.length > 0 ? (
                <div className="space-y-2">
                  {tradingLogs.map((log, index) => (
                    <div
                      key={log.id}
                      className="py-3 px-4 grid grid-cols-7 gap-4 items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%)',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        borderRadius: '8px',
                        fontSize: Typography.BodySmall
                      }}
                    >
                      <div className="font-mono text-xs" style={{ color: '#1e293b' }}>
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </div>
                      <div className="font-mono font-medium" style={{
                        color: '#1e293b',
                        fontSize: Typography.Fine,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {log.action}
                      </div>
                      <div className="font-mono" style={{ color: '#1e293b' }}>
                        {log.pair}
                      </div>
                      <div className="font-mono" style={{ color: '#1e293b', fontSize: Typography.Fine }}>
                        {log.exchange}
                      </div>
                      <div className="font-mono text-right" style={{ color: '#1e293b', fontSize: Typography.Fine }}>
                        {log.amount}
                      </div>
                      <div className="font-mono text-right" style={{ color: '#1e293b', fontSize: Typography.Fine }}>
                        ${log.price}
                      </div>
                      <div className="font-mono text-right font-medium" style={{ color: '#10b981', fontSize: Typography.Fine }}>
                        +${log.profit}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Activity size={56} className="mx-auto mb-4" style={{ color: '#1e293b', opacity: 0.3 }} />
                  <p className="mb-2" style={{ color: '#1e293b', fontSize: Typography.HeadlineSmall, fontWeight: Typography.WeightBold }}>{t('robot.noActivity')}</p>
                  <p style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.BodyMedium }}>{t('robot.activateRobot')}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12" style={{ color: '#1e293b', fontSize: Typography.BodyLarge }}>
              <BarChart3 size={48} className="mx-auto mb-4" style={{ opacity: 0.5, color: '#1e293b' }} />
              {t('robot.clickToViewLogs')}
            </div>
          )}
        </div>

      {showInvestmentTiers && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}>
          <div className="max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15)'
          }}>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2" style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BarChart3 size={24} style={{ color: '#ffffff' }} />
                </div>
                <h3 style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#1e293b' }}>{t('robot.investmentTiers')}</h3>
              </div>
              <button
                onClick={() => setShowInvestmentTiers(false)}
                className="hover:opacity-70 transition-opacity"
                style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div className="text-center mb-8">
              <h2 className="mb-4" style={{ fontSize: Typography.HeadlineLarge, fontWeight: Typography.WeightBold, color: '#1e293b' }}>
                Invest More. Earn More.
              </h2>
              <p className="max-w-2xl mx-auto" style={{ color: '#64748b', fontSize: Typography.BodyMedium, lineHeight: Typography.LeadingNormal }}>
                You want to make higher profit? No problem! Depending on your investment you are able to trade with a certain leverage. The higher your leverage, the higher your daily profit.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* SHRIMP Tier */}
              <div className="p-6 text-center" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
              }}>
                <div className="flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🦐</span>
                </div>
                <h3 className="mb-2 font-mono" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>SHRIMP</h3>
                <div className="mb-4 font-mono" style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#10b981' }}>0.2 - 0.5%</div>
                <div className="mb-2" style={{ color: '#64748b', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Minimum Investment</div>
                <div className="font-mono" style={{ color: '#1e293b', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>0.01 BTC</div>
              </div>

              {/* CRAB Tier */}
              <div className="p-6 text-center" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
              }}>
                <div className="flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🦀</span>
                </div>
                <h3 className="mb-2 font-mono" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>CRAB</h3>
                <div className="mb-4 font-mono" style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#10b981' }}>0.35 - 0.7%</div>
                <div className="mb-2" style={{ color: '#64748b', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Minimum Investment</div>
                <div className="font-mono" style={{ color: '#1e293b', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>0.1 BTC</div>
              </div>

              {/* OCTOPUS Tier */}
              <div className="p-6 text-center" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
              }}>
                <div className="flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🐙</span>
                </div>
                <h3 className="mb-2 font-mono" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>OCTOPUS</h3>
                <div className="mb-4 font-mono" style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#10b981' }}>0.6 - 1%</div>
                <div className="mb-2" style={{ color: '#64748b', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Minimum Investment</div>
                <div className="font-mono" style={{ color: '#1e293b', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>1 BTC</div>
              </div>

              {/* DOLPHIN Tier */}
              <div className="p-6 text-center" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
              }}>
                <div className="flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🐬</span>
                </div>
                <h3 className="mb-2 font-mono" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>DOLPHIN</h3>
                <div className="mb-4 font-mono" style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#10b981' }}>0.8 - 1.3%</div>
                <div className="mb-2" style={{ color: '#64748b', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Minimum Investment</div>
                <div className="font-mono" style={{ color: '#1e293b', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>5 BTC</div>
              </div>

              {/* SHARK Tier */}
              <div className="p-6 text-center" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
              }}>
                <div className="flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🦈</span>
                </div>
                <h3 className="mb-2 font-mono" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>SHARK</h3>
                <div className="mb-4 font-mono" style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#10b981' }}>1.2 - 2.6%</div>
                <div className="mb-2" style={{ color: '#64748b', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Minimum Investment</div>
                <div className="font-mono" style={{ color: '#1e293b', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>10 BTC</div>
              </div>

              {/* WHALE Tier */}
              <div className="p-6 text-center" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
              }}>
                <div className="flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">🐋</span>
                </div>
                <h3 className="mb-2 font-mono" style={{ fontSize: Typography.HeadlineMedium, fontWeight: Typography.WeightBold, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>WHALE</h3>
                <div className="mb-4 font-mono" style={{ fontSize: Typography.DisplayMedium, fontWeight: Typography.WeightBold, color: '#10b981' }}>2.1 - 4.2%</div>
                <div className="mb-2" style={{ color: '#64748b', fontSize: Typography.Caption, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Minimum Investment</div>
                <div className="font-mono" style={{ color: '#1e293b', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>50 BTC</div>
              </div>
            </div>

            <div className="p-6 text-center" style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.15)',
              borderRadius: '16px'
            }}>
              <div className="mb-6">
                <p className="mb-2" style={{ color: '#1e293b', fontSize: Typography.BodySmall }}>
                  <strong>Daily Profit Calculation:</strong> Profits are calculated based on your allocated balance and tier.
                </p>
                <p style={{ color: '#64748b', fontSize: Typography.BodySmall }}>
                  The robot executes approximately 720 trades per day (1 trade every 2 minutes) to achieve your tier's daily profit target.
                </p>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setShowInvestmentTiers(false)}
                  className="px-6 py-3 font-medium hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: 'rgba(100, 116, 139, 0.1)',
                    color: '#64748b',
                    border: '1px solid rgba(100, 116, 139, 0.2)',
                    borderRadius: '10px',
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightMedium,
                    cursor: 'pointer'
                  }}
                >
                  Return
                </button>
                <button
                  onClick={() => {
                    setShowInvestmentTiers(false);
                    setShowAllocationForm(true);
                  }}
                  className="px-6 py-3 hover:opacity-90 transition-opacity"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightSemibold,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)'
                  }}
                >
                  Invest Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArbitrageRobotPage;